// Paginated échéancier of the active simulation.
//
// Page size is 4, the contract value of the design pack, and `total` feeds the
// "1-4 sur 24 échéances" counter of HzPagination. Without filter and with the
// default sort the rows are paginated by PostgREST, which keeps the payload
// minimal. As soon as a filter or another sort is applied the full set is loaded
// once and sorted in memory, so the counter keeps matching what is displayed
// instead of counting rows the advisor filtered out.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  ErreurHorizon,
  listerEcheances,
  toErreur,
  toutesEcheances,
  transitionEcheance,
} from '@horizon/api-client';
import type { ActionTransition, Echeance, StatutEcheance, TypeEcheance } from '@horizon/types';

/** Page size of the échéancier, aligned with the design reference. */
export const TAILLE_PAGE_DEFAUT = 4;

export type ChampTri = 'date_echeance' | 'montant' | 'statut';
export type SensTri = 'asc' | 'desc';

export interface FiltresEcheancier {
  readonly statut: StatutEcheance | 'tous';
  readonly type: TypeEcheance | 'tous';
}

const FILTRES_NEUTRES: FiltresEcheancier = { statut: 'tous', type: 'tous' };

/** Display order of the statuses, used by the "statut" sort. */
const RANG_STATUT: Readonly<Record<StatutEcheance, number>> = {
  en_cours: 0,
  planifie: 1,
  valide: 2,
  rejete: 3,
  annule: 4,
};

function comparer(a: Echeance, b: Echeance, champ: ChampTri): number {
  if (champ === 'montant') {
    return (a.montant ?? 0) - (b.montant ?? 0);
  }
  if (champ === 'statut') {
    return (RANG_STATUT[a.statut] ?? 0) - (RANG_STATUT[b.statut] ?? 0);
  }
  return a.date_echeance.localeCompare(b.date_echeance);
}

export const useEcheancierStore = defineStore('echeancier', () => {
  const simulationId = ref<string | null>(null);
  const lignes = ref<readonly Echeance[]>([]);
  const total = ref(0);
  const page = ref(1);
  const taille = ref(TAILLE_PAGE_DEFAUT);
  const filtres = ref<FiltresEcheancier>({ ...FILTRES_NEUTRES });
  const champTri = ref<ChampTri>('date_echeance');
  const sensTri = ref<SensTri>('asc');

  const chargement = ref(false);
  const transitionEnCours = ref<string | null>(null);
  const erreur = ref<ErreurHorizon | null>(null);

  let jeton = 0;

  const filtreActif = computed(
    () => filtres.value.statut !== 'tous' || filtres.value.type !== 'tous',
  );
  const triParDefaut = computed(
    () => champTri.value === 'date_echeance' && sensTri.value === 'asc',
  );
  const paginationServeur = computed(() => !filtreActif.value && triParDefaut.value);
  const nombrePages = computed(() => Math.max(1, Math.ceil(total.value / taille.value)));
  const estVide = computed(() => !chargement.value && erreur.value === null && total.value === 0);
  const erreurReseau = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && erreur.value.reseau ? erreur.value : null,
  );
  const erreurServeur = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && !erreur.value.reseau ? erreur.value : null,
  );

  /** Apply the filters, the sort and the pagination to a full set held in memory. */
  function appliquerLocalement(source: readonly Echeance[]): void {
    const retenues = source.filter(
      (ligne) =>
        (filtres.value.statut === 'tous' || ligne.statut === filtres.value.statut) &&
        (filtres.value.type === 'tous' || ligne.type === filtres.value.type),
    );
    const signe = sensTri.value === 'asc' ? 1 : -1;
    const triees = [...retenues].sort((a, b) => signe * comparer(a, b, champTri.value));
    total.value = triees.length;
    const debut = (page.value - 1) * taille.value;
    lignes.value = triees.slice(debut, debut + taille.value);
  }

  /** Reload the current page from the server, honouring the active filters. */
  async function rafraichir(): Promise<void> {
    const identifiant = simulationId.value;
    if (identifiant === null) {
      lignes.value = [];
      total.value = 0;
      return;
    }
    jeton += 1;
    const courant = jeton;
    chargement.value = true;
    erreur.value = null;
    try {
      if (paginationServeur.value) {
        const resultat = await listerEcheances(identifiant, page.value - 1, taille.value);
        if (courant !== jeton) {
          return;
        }
        lignes.value = resultat.lignes;
        total.value = resultat.total;
      } else {
        const ensemble = await toutesEcheances(identifiant);
        if (courant !== jeton) {
          return;
        }
        appliquerLocalement(ensemble);
      }
      // A deletion elsewhere can leave the advisor past the last page.
      if (page.value > nombrePages.value) {
        page.value = nombrePages.value;
        await rafraichir();
      }
    } catch (cause) {
      if (courant !== jeton) {
        return;
      }
      erreur.value = toErreur(cause);
      lignes.value = [];
      total.value = 0;
    } finally {
      if (courant === jeton) {
        chargement.value = false;
      }
    }
  }

  /** Attach the store to a simulation and load its first page. */
  async function charger(identifiant: string): Promise<void> {
    if (simulationId.value !== identifiant) {
      page.value = 1;
    }
    simulationId.value = identifiant;
    await rafraichir();
  }

  async function allerALaPage(valeur: number): Promise<void> {
    const cible = Math.min(Math.max(1, Math.round(valeur)), nombrePages.value);
    if (cible === page.value) {
      return;
    }
    page.value = cible;
    await rafraichir();
  }

  async function definirFiltres(valeur: Partial<FiltresEcheancier>): Promise<void> {
    filtres.value = { ...filtres.value, ...valeur };
    page.value = 1;
    await rafraichir();
  }

  async function reinitialiserFiltres(): Promise<void> {
    filtres.value = { ...FILTRES_NEUTRES };
    page.value = 1;
    await rafraichir();
  }

  /** Sort by a column; asking twice for the same column flips the direction. */
  async function trier(champ: ChampTri): Promise<void> {
    if (champTri.value === champ) {
      sensTri.value = sensTri.value === 'asc' ? 'desc' : 'asc';
    } else {
      champTri.value = champ;
      sensTri.value = 'asc';
    }
    page.value = 1;
    await rafraichir();
  }

  async function definirTaille(valeur: number): Promise<void> {
    taille.value = Math.max(1, Math.round(valeur));
    page.value = 1;
    await rafraichir();
  }

  /**
   * Apply a state transition of the RG-11 machine, then reload the page so the
   * row, the counter and the progression stay consistent. The caller renders the
   * confirmation modal for the destructive actions and the success toast.
   */
  async function appliquerTransition(
    id: string,
    action: ActionTransition,
    nouvelleDate?: string,
  ): Promise<Echeance | null> {
    transitionEnCours.value = id;
    erreur.value = null;
    try {
      const sortie =
        nouvelleDate === undefined
          ? await transitionEcheance(id, action)
          : await transitionEcheance(id, action, nouvelleDate);
      await rafraichir();
      return sortie.echeance;
    } catch (cause) {
      erreur.value = toErreur(cause);
      return null;
    } finally {
      transitionEnCours.value = null;
    }
  }

  /** Replay the current read, wired to the retry action of the network banner. */
  async function reessayer(): Promise<void> {
    await rafraichir();
  }

  function reinitialiser(): void {
    jeton += 1;
    simulationId.value = null;
    lignes.value = [];
    total.value = 0;
    page.value = 1;
    taille.value = TAILLE_PAGE_DEFAUT;
    filtres.value = { ...FILTRES_NEUTRES };
    champTri.value = 'date_echeance';
    sensTri.value = 'asc';
    erreur.value = null;
    chargement.value = false;
    transitionEnCours.value = null;
  }

  return {
    simulationId,
    lignes,
    total,
    page,
    taille,
    filtres,
    champTri,
    sensTri,
    chargement,
    transitionEnCours,
    erreur,
    filtreActif,
    nombrePages,
    estVide,
    erreurReseau,
    erreurServeur,
    charger,
    rafraichir,
    allerALaPage,
    definirFiltres,
    reinitialiserFiltres,
    trier,
    definirTaille,
    appliquerTransition,
    reessayer,
    reinitialiser,
  };
});
